// adresstypen.ts — was für eine Anschrift das ist.
//
// Die Ferienimmobilie ist KEIN Zweitwohnsitz: anderer Zweck, andere Nutzung,
// andere Risiken — und man kann mehrere haben. Genau das war der Anlass für
// diese Liste; bisher ließen sich Zusatzadressen nur stapeln, ohne zu sagen,
// was sie sind.
//
// Die `value` sind Speicherwerte (`ContactAddress.addressType`) und dürfen sich
// nicht ändern; die Beschriftungen dürfen es.
//
// Der Erstwohnsitz steht NICHT in dieser Liste: Er ist die Standardadresse und
// über `isDefault` gekennzeichnet. Ihn zusätzlich als Typ zu führen hieße, zwei
// Quellen für dieselbe Aussage zu haben.

export const ADRESS_TYPEN: ReadonlyArray<{ value: string; label: string }> = [
  { value: "zweitwohnsitz", label: "Zweitwohnsitz" },
  { value: "postanschrift", label: "Abweichende Postanschrift" },
  { value: "ferienimmobilie", label: "Ferienimmobilie" },
  { value: "sonstige", label: "Sonstige" },
];

/** Beschriftung zu einem gespeicherten Wert — Unbekanntes bleibt sichtbar. */
export const adresstypLabel = (wert?: string | null): string => {
  const w = String(wert ?? "").trim();
  if (w === "geschaeftlich") return "Geschäftlich";
  if (w === "sonstige") return "Sonstige";
  return ADRESS_TYPEN.find((t) => t.value === w)?.label ?? w;
};

/** Mehrfach erlaubt? Von einer Ferienimmobilie kann es mehrere geben. */
export const MEHRFACH_ERLAUBT = new Set(["ferienimmobilie", "geschaeftlich", "sonstige"]);


// ── Eigener Name einer Anschrift (T-358) ──────────────────────────────────
//
// `ContactAddress.addressDescription` ist zweideutig: Die Personenmaske schrieb
// bei leerem Namen die Art-Beschriftung hinein („Ferienimmobilie"), damit die
// Spalte nie leer ist. Ein eigener Name („Hanselhof") steht in derselben
// Spalte. Diese Helfer trennen beides — sonst stünde „Ferienimmobilie" als Name
// im Eingabefeld, und nach einem Artwechsel bliebe die alte Beschriftung hängen.

/** Beschriftungen, die die Maske selbst vergibt — kein eigener Name. */
const AUTO_BESCHRIFTUNGEN = new Set(
  [...ADRESS_TYPEN.map((t) => t.label), "Geschäftlich", "Weitere Anschrift", "Wohnanschrift"].map((t) =>
    t.toLocaleLowerCase("de"),
  ),
);

/** Ist der Text nur eine automatisch vergebene Beschriftung (oder leer)? */
export const istAutoBeschriftung = (text?: string | null): boolean => {
  const t = String(text ?? "").trim();
  return !t || AUTO_BESCHRIFTUNGEN.has(t.toLocaleLowerCase("de"));
};

/** Der eigene Name aus `addressDescription` — leer, wenn dort nur die Art steht. */
export const eigenerAnschriftName = (addressDescription?: string | null): string =>
  istAutoBeschriftung(addressDescription) ? "" : String(addressDescription ?? "").trim();

/**
 * Anzeige einer Anschrift in Übersichten: Art und Name zusammen
 * („Ferienimmobilie · Hanselhof"). Eine veraltete Auto-Beschriftung verliert
 * gegen die gespeicherte Art; ohne Art bleibt die Beschreibung, wie sie ist
 * („Wohnanschrift", „Lager").
 */
export const anschriftBezeichnung = (a: {
  addressDescription?: string | null;
  addressType?: string | null;
}): string => {
  const art = String(a.addressType ?? "").trim() ? adresstypLabel(a.addressType) : "";
  const name = eigenerAnschriftName(a.addressDescription);
  if (art && name) return `${art} · ${name}`;
  return art || name || String(a.addressDescription ?? "").trim();
};
